/**
 * Approvals Hook
 * Manages approval operations and pending approvals
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthToken } from './use-auth';

// Types
export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CHANGES_REQUESTED';

export type ArtifactType = 
  | 'baseSpec' 
  | 'contract' 
  | 'technicalSpec' 
  | 'release'
  | 'goal'
  | 'persona'
  | 'useCase'
  | 'journey'
  | 'serviceMap'
  | 'storyboard';

export interface Approval {
  id: string;
  approverId: string;
  artifactType: ArtifactType;
  artifactId: string;
  status: ApprovalStatus;
  comments: string | null;
  decidedAt: string | null;
  createdAt: string;
  approver: {
    id: string;
    name: string | null;
    email: string;
  };
  artifact?: {
    id: string;
    title: string;
    version?: string;
    status: string;
  };
}

export interface ApprovalDecision {
  status: 'APPROVED' | 'REJECTED' | 'CHANGES_REQUESTED';
  comments?: string;
}

// List approvals
export function useApprovals(projectId: string, filters?: { status?: ApprovalStatus; artifactType?: ArtifactType }) {
  const token = useAuthToken();
  
  return useQuery({
    queryKey: ['approvals', projectId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.artifactType) params.append('artifactType', filters.artifactType);
      
      const response = await fetch(`/api/projects/${projectId}/approvals?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch approvals');
      const data = await response.json();
      return data.data as Approval[];
    },
    enabled: !!projectId && !!token,
  });
}

// Get pending approvals
export function usePendingApprovals(projectId: string) {
  const token = useAuthToken();
  
  return useQuery({
    queryKey: ['approvals', projectId, 'pending'],
    queryFn: async () => {
      const response = await fetch(`/api/projects/${projectId}/approvals/pending`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch pending approvals');
      const data = await response.json();
      return data.data as Approval[];
    },
    enabled: !!projectId && !!token,
  });
}

// Make approval decision
export function useApproveDecision(projectId: string) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ approvalId, decision }: { approvalId: string; decision: ApprovalDecision }) => {
      const response = await fetch(`/api/projects/${projectId}/approvals/${approvalId}/decide`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(decision),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to process approval decision');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approvals', projectId] });
      queryClient.invalidateQueries({ queryKey: ['base-specs', projectId] });
      queryClient.invalidateQueries({ queryKey: ['contracts', projectId] });
      queryClient.invalidateQueries({ queryKey: ['technical-specs', projectId] });
      queryClient.invalidateQueries({ queryKey: ['releases', projectId] });
    },
  });
}
